import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, ArrowLeft, ArrowRight, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../components/ui/sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'envoi');
      }

      setSubmitted(true);
      toast.success('Email envoyé !');
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-white mb-4">
              <ShieldCheck className="w-10 h-10 text-fgs-primary" />
              <span className="font-heading text-3xl font-bold">FGS</span>
            </div>
          </div>

          {/* Success Card */}
          <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 md:p-8 text-center">
            <div className="w-16 h-16 bg-fgs-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-fgs-primary" />
            </div>
            
            <h1 className="font-heading text-2xl font-bold text-white mb-4">
              Email envoyé !
            </h1>
            
            <p className="text-fgs-text-muted mb-6">
              Si un compte existe avec l'adresse <span className="text-white font-medium">{email}</span>, 
              vous recevrez un email avec un lien pour réinitialiser votre mot de passe.
            </p>
            
            <p className="text-fgs-text-muted text-sm mb-6">
              N'oubliez pas de vérifier vos spams si vous ne voyez pas l'email dans votre boîte de réception.
            </p>

            <Link to="/auth">
              <Button className="w-full bg-fgs-primary hover:bg-fgs-primary-hover">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-white mb-4">
            <ShieldCheck className="w-10 h-10 text-fgs-primary" />
            <span className="font-heading text-3xl font-bold">FGS</span>
          </div>
          <p className="text-fgs-text-muted">Récupération de mot de passe</p>
        </div>

        {/* Form Card */}
        <div className="bg-fgs-surface border border-fgs-border rounded-xl p-6 md:p-8">
          <h1 className="font-heading text-xl font-bold text-white mb-2">
            Mot de passe oublié ?
          </h1>
          <p className="text-fgs-text-muted text-sm mb-6">
            Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
          </p>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-fgs-accent/10 border border-fgs-accent/30 rounded-lg text-fgs-accent text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-fgs-text-muted text-sm">Adresse email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-fgs-text-muted" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="votre@email.com"
                  className="pl-10 bg-fgs-bg border-fgs-border text-white placeholder:text-fgs-text-muted"
                  data-testid="forgot-email"
                />
              </div>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-fgs-primary hover:bg-fgs-primary-hover h-12 font-semibold"
              data-testid="forgot-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Envoyer le lien
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Back Link */}
          <div className="mt-6 text-center">
            <Link 
              to="/auth" 
              className="inline-flex items-center text-sm text-fgs-text-muted hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
